import { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, refreshCsrf } from '@/api/client';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { MaintenanceBanner } from '@/layout/MaintenanceBanner';
import styles from './AuthPages.module.css';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [showRecovery, setShowRecovery] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await refreshCsrf();
      const body: Record<string, string> = { email, password };
      if (showRecovery) {
        const code = recoveryCode.replace(/\s+/g, '');
        if (code) body.recoveryCode = code;
      } else {
        const totpDigits = totp.replace(/\D/g, '');
        if (totpDigits) body.totp = totpDigits;
      }
      const res = await apiFetch<{
        requiresTotpSetup?: boolean;
        user: { totpEnabled: boolean };
      }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuthRedirect: true,
      });
      await refresh();
      if (res.requiresTotpSetup) {
        navigate('/2fa/setup');
        return;
      }
      const next = params.get('next') || '/app/dashboard';
      navigate(next);
    } catch {
      setError('Inloggen mislukt. Controleer gegevens, 2FA-code of herstelcode.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <MaintenanceBanner />
      <form className={styles.card} onSubmit={(e) => void onSubmit(e)}>
        <h1 className={styles.heading}>Inloggen</h1>
        <p className={styles.sub}>Marketing Hub — sessie via beveiligde cookie.</p>
        <Field label="E-mailadres" error={error}>
          <TextInput
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Wachtwoord">
          <TextInput
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {!showRecovery ? (
          <Field label="2FA-code (indien actief)" hint="6 cijfers uit je authenticator-app">
            <TextInput
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          </Field>
        ) : (
          <Field label="Herstelcode" hint="Eenmalige backup-code">
            <TextInput value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value)} />
          </Field>
        )}
        <div className={styles.row}>
          <button
            type="button"
            className={styles.linkBtn}
            onClick={() => {
              setShowRecovery((open) => !open);
              setTotp('');
              setRecoveryCode('');
            }}
          >
            {showRecovery ? 'Gebruik 2FA-code' : 'Gebruik herstelcode'}
          </button>
        </div>
        <Button type="submit" fullWidth disabled={loading}>
          {loading ? 'Bezig…' : 'Inloggen'}
        </Button>
        <p className={styles.sub}>
          Eerste keer? Na inloggen rond je <Link to="/2fa/setup">2FA-setup</Link> af.
        </p>
      </form>
    </div>
  );
}
